import React, { useState } from 'react';
import './SummaryView.css';
import './metallicEffects.css';
import { shouldShowSingleLine } from './fontBusinessRules';

interface SummaryData {
    quantity: number;
    napkinColor: string;
    colorText: string;
    line1: string;
    line2: string;
    font: string;
    image: string;
}

interface SummaryViewProps {
    data: SummaryData;
}

const SummaryView: React.FC<SummaryViewProps> = ({ data }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const pricePerNapkin = 1.50;
    const total = data.quantity * pricePerNapkin;
    
    // Check if second line should be hidden based on font and image combination
    const hideSecondLine = shouldShowSingleLine(data.font, data.image);

    const handleCheckout = async () => {
        setIsProcessing(true);
        setError(null);

        try {
            // Prepare order data
            const orderData = {
                quantity: data.quantity,
                napkinColor: data.napkinColor,
                foilColor: data.colorText,
                textLineOne: data.line1,
                textLineTwo: hideSecondLine ? '' : data.line2,
                font: data.font,
                image: data.image,
                orientation: 'square' // You might want to pass this from state
            };

            // Call the API to create checkout session
            const apiUrl = process.env.NODE_ENV === 'production' 
                ? '/api/CreateCheckoutSession'
                : 'http://localhost:7071/api/CreateCheckoutSession';

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData)
            });

            if (!response.ok) {
                throw new Error('Failed to create checkout session');
            }

            const { checkoutUrl } = await response.json();

            // Redirect to Stripe Checkout
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
            } else {
                throw new Error('No checkout URL received');
            }
        } catch (err: any) {
            console.error('Checkout error:', err);
            setError('Failed to process checkout. Please try again.');
            setIsProcessing(false);
        }
    };


    return (
        <div className="summary-view professional-summary">
            <div className="summary-header">
                <h3>Order Summary</h3>
            </div>
            
            <div className="summary-content">
                <div className="summary-row">
                    <span className="summary-label">Quantity</span>
                    <span className="summary-value">{data.quantity} napkins</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Napkin Color</span>
                    <span className="summary-value">{data.napkinColor}</span>
                </div>
                <div className="summary-row">
                    <span className="summary-label">Print Color</span>
                    <span className="summary-value">{data.colorText}</span>
                </div>
                {data.line1 && (
                    <div className="summary-row">
                        <span className="summary-label">Text Line 1</span>
                        <span className="summary-value">{data.line1}</span>
                    </div>
                )}
                {data.line2 && !hideSecondLine && (
                    <div className="summary-row">
                        <span className="summary-label">Text Line 2</span>
                        <span className="summary-value">{data.line2}</span>
                    </div>
                )}
                <div className="summary-row">
                    <span className="summary-label">Font</span>
                    <span className="summary-value">{data.font}</span>
                </div>
                {data.image && data.image !== 'a0' && (
                    <div className="summary-row">
                        <span className="summary-label">Design</span>
                        <span className="summary-value">Custom Image {data.image.toUpperCase()}</span>
                    </div>
                )}
                <div className="summary-divider"></div>
                <div className="summary-row summary-total">
                    <span className="summary-label">Total</span>
                    <span className="summary-value total-price">${total.toFixed(2)}</span>
                </div>
                {error && (
                    <div style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
                        {error}
                    </div>
                )}
                <button 
                    className="checkout-button" 
                    onClick={handleCheckout}
                    disabled={isProcessing}
                    style={{
                        opacity: isProcessing ? 0.7 : 1,
                        cursor: isProcessing ? 'wait' : 'pointer'
                    }}
                >
                    {isProcessing ? 'Processing...' : 'Proceed to Checkout'}
                </button>
            </div>
        </div>
    );
};

export default SummaryView;
